import {Building as SatisfactoryBuilding, Item as SatisfactoryItem} from "./Satisfactory/types";
import {Building as DysonSphereBuilding, Item as DysonSphereItem} from './DSP/types';
import {ProliferatorType} from "./DSP/types";

export interface FactoryGame { type: string }
export interface Satisfactory extends FactoryGame { type: 'Satisfactory' }
export interface DysonSphere extends FactoryGame { type: 'DysonSphere' }

export type AnyFactoryGame = Satisfactory | DysonSphere;

export enum FactoryGameType {
    Satisfactory = 'Satisfactory',
    DysonSphere = 'DysonSphere',
}

export type Item<Game extends FactoryGame = AnyFactoryGame> = Game extends Satisfactory ? SatisfactoryItem : DysonSphereItem;
export type Building<Game extends FactoryGame = AnyFactoryGame> = Game extends Satisfactory ? SatisfactoryBuilding : DysonSphereBuilding;

export type ItemStack<Game extends FactoryGame = AnyFactoryGame> = {
    item: Item<Game>,
    amount: number,
}

/**
 * Normalized IO - all values in item/min.
 */
export type VirtualNormalizedRecipe<Game extends FactoryGame = AnyFactoryGame> = {
    ingredients: ItemStack<Game>[],
    products: ItemStack<Game>[],
}

export type Recipe<Game extends FactoryGame = AnyFactoryGame> = {
    recipeType: string,
    ingredients: ItemStack<Game>[],
    products: ItemStack<Game>[],
    duration: number,
    building: Building<Game>,
}

export type IFacility<Game extends FactoryGame = AnyFactoryGame> = Factory<Game> | Facility<Game>;
export type FacilityType = 'Facility'|'Factory';

export type Facility<Game extends FactoryGame = AnyFactoryGame> = {
    id: number,
    type: 'Facility',
    parentFacility?: IFacility<Game>,
    name: string,
    game: Game extends Satisfactory ? FactoryGameType.Satisfactory : FactoryGameType.DysonSphere,
    facilities: IFacility<Game>[],
}

export type Factory<Game extends FactoryGame = AnyFactoryGame> = Omit<Facility<Game>, 'type'> & SpecificFactoryData<Game> & {
    type: 'Factory',
    recipeType: string,
    amount: number,
    recipe?: Recipe<Game>,
};

export type AddFacilityPayload = {
    type: 'Facility',
    name: string,
    parentFacility: null|number,
}

export type SpecificFactoryData<Game extends FactoryGame = AnyFactoryGame> = Game extends Satisfactory ? {
    clockSpeed: number,
} : {
    machineTier: number,
    proliferatorTier: number,
    proliferatorType: ProliferatorType,
}

export type AddFactoryPayload<Game extends FactoryGame = AnyFactoryGame> = SpecificFactoryData<Game> & {
    type: 'Factory',
    name: string,
    parentFacility: number,
    recipeType: string,
    amount: number,
};

export type AddFacilityUserInput = Omit<AddFacilityPayload, 'parentFacility'>;
export type AddFactoryUserInput<Game extends FactoryGame = AnyFactoryGame> = Omit<AddFactoryPayload<Game>, 'parentFacility'>;

export type EditFacilityUserInput = Omit<AddFacilityUserInput, 'type'>;
export type EditFactoryUserInput<Game extends FactoryGame = AnyFactoryGame> = Omit<AddFactoryUserInput<Game>, 'type'>;

export type EditFacilityPayload = Omit<AddFacilityPayload, 'type'> & { id: number };
export type EditFactoryPayload<Game extends FactoryGame = AnyFactoryGame> = Omit<AddFactoryPayload<Game>, 'type'> & { id: number };
